import { Filter } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import { TaskType } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { CallIntelTimePeriodLabelToType, timePeriodLabels } from '../constant';
import * as CallIntelTypes from '../types';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);
/**
 * To get the drill down filter keys for overview page
 */
export const getDrillDownFilterKeys = ({
  id,
  type,
}: CallIntelTypes.CallIntelSubView): CallIntelTypes.DrillDownOptions | undefined => {
  if (!id || !type) return undefined;

  switch (type) {
    case 'appointment-type':
      return {
        index: CallIntelTypes.FilterTypeEnum.FILTER_BY_APPOINTMENT_TYPE,
        key: 'appointment_types',
        value: [id],
      };

    case 'category':
      return {
        index: CallIntelTypes.FilterTypeEnum.FILTER_BY_CATEGORY,
        key: 'categories',
        value: [id],
      };

    case 'sentiment':
      return {
        index: CallIntelTypes.FilterTypeEnum.FILTER_BY_SENTIMENT,
        key: 'sentiments',
        value: [id],
      };

    case 'service-quality':
      return {
        index: CallIntelTypes.FilterTypeEnum.FILTER_BY_SERVICE_QUALITY,
        key: 'service_quality_flags',
        value: [id],
      };

    default:
      return undefined;
  }
};

// To get the total number of calls
export const getCallsCount = (
  overview?: CallIntelTypes.Overview,
  sentimentCode?: CallIntelTypes.CallIntelSubViewId
): number => {
  if (sentimentCode) {
    return overview?.sentiments?.[sentimentCode as CallIntelTypes.SentimentEnum] || 0;
  }

  // Each call will have only 1 sentiment, hence total calls = total sentiments
  return Object.values(overview?.sentiments || {}).reduce((acc, count) => acc + count, 0);
};

// Defines if the call is still in processing state
export const isCallInProcessing = (status?: CallIntelTypes.CallStatus): boolean =>
  status === CallIntelTypes.CallStatus.CALL_STATUS_INFERENCING ||
  status === CallIntelTypes.CallStatus.CALL_STATUS_PENDING ||
  status === CallIntelTypes.CallStatus.CALL_STATUS_TRANSCRIBING;

// Identify if the call quality is bad
// mosScore : 1 - 3.5 Poor Quality
// mosScore : > 3.5 to 5 Good Quality
export const isPoorAudio = (call?: CallIntelTypes.Call | null): boolean => {
  if (!call?.mosScore) {
    // Older records might not have mosScore hence do not treat them as of bad quality
    return false;
  }

  return call.mosScore >= 1 && call.mosScore <= 3.5;
};

export const isFailedCall = (status?: CallIntelTypes.CallStatus): boolean => {
  return (
    status === CallIntelTypes.CallStatus.CALL_STATUS_FAILED || status === CallIntelTypes.CallStatus.CALL_STATUS_ERROR
  );
};

export const isSkippedCall = (status?: CallIntelTypes.CallStatus): boolean => {
  return status === CallIntelTypes.CallStatus.CALL_STATUS_SKIPPED;
};

export const getPhoneNumber = (phoneNumber?: CallIntelTypes.PhoneNumber): string => {
  if (!phoneNumber) return '';

  const { countryCode, nationalNumber } = phoneNumber;
  return `${countryCode}${nationalNumber}`;
};

export const isUserInactive = (_officeUser?: CallIntelTypes.OfficeUser | null): boolean => {
  // TODO :: add inactive user logic when we have one, for now return false for all users
  return false;
};

export const isOfficeUserDataUnavailable = (recordDate?: string): boolean => {
  // As confirmed by the BE, office user data is not available prior to 1 April 2024 (including this date)
  // Hence, hard coding the date to 2nd April 2024
  const unavailableOfficeUserDataDate = new Date(2024, 3, 2);
  return !!recordDate && dayjs(recordDate).isBefore(dayjs(unavailableOfficeUserDataDate));
};

export const transformEditableFields = (
  fields: Partial<CallIntelTypes.CallEditableFields>,
  type: CallIntelTypes.TransformEditableFieldType = 'edit'
): Record<string, any> => {
  const transformed: Record<string, any> = {};

  const keyMap: Partial<Record<keyof CallIntelTypes.CallEditableFields, string>> = {
    contactType: 'contact_type',
    schedulingOutcome: 'scheduling_outcome',
    schedulingOpportunity: 'scheduling_opportunity',
    categories: 'categories',
    schedulingOutcomeV2: 'scheduling_outcome_v2',
  };

  // direct mappings
  for (const [key, transformedKey] of Object.entries(keyMap) as [keyof CallIntelTypes.CallEditableFields, string][]) {
    if (Object.hasOwn(fields, key)) {
      transformed[transformedKey] = fields[key];
    }
  }

  if (Object.hasOwn(fields, 'callSentiment')) {
    if (fields.callSentiment === null) {
      transformed.call_sentiment = null;
    } else {
      transformed.call_sentiment =
        type === 'edit'
          ? { sentiment: fields.callSentiment }
          : { sentiment: fields.callSentiment?.sentiment ?? fields.callSentiment };
    }
  }

  if (Object.hasOwn(fields, 'appointmentTypes')) {
    if (fields.appointmentTypes === null) {
      transformed.appointment_types = { appointment_type: [] };
    } else {
      transformed.appointment_types =
        type === 'edit'
          ? { appointment_type: fields.appointmentTypes }
          : { appointment_type: fields.appointmentTypes?.appointmentType };
    }
  }

  if (fields.serviceQualityFlags) {
    transformed.service_quality_flags = fields.serviceQualityFlags.map((flag) => ({
      service_quality_flag: type === 'edit' ? flag : flag.serviceQualityFlag,
      is_disabled: false,
    }));
  }

  return transformed;
};

export const generateListTasksPayload = (
  callTaskParams: CallIntelTypes.CallTaskParams
): CallIntelTypes.CallTaskListRequest => {
  const payload: CallIntelTypes.CallTaskListRequest = {
    filter: {
      [CallIntelTypes.TaskFilterTypeEnum.FILTER_BY_LOCATION]: {
        locationId: callTaskParams.locations || [],
      },
      [CallIntelTypes.TaskFilterTypeEnum.FILTER_BY_SOURCE]: {
        source: callTaskParams.source || CallIntelTypes.TaskSourceEnum.SOURCE_CALL_INTELLIGENCE,
      },
      [CallIntelTypes.TaskFilterTypeEnum.FILTER_BY_SOURCE_ID]: {
        sourceId: callTaskParams.callIds,
      },
    },
    includeMetadata: callTaskParams.includeMetadata ?? false,
    sortType: callTaskParams.sortType ?? CallIntelTypes.TaskSortTypeEnum.SORT_DESC,
    isDemo: callTaskParams.isDemo ?? false,
    demoMetadata: callTaskParams.demoMetadata ?? undefined,
  };
  if (payload.filter && callTaskParams?.taskStatusFilters?.length) {
    payload.filter[CallIntelTypes.TaskFilterTypeEnum.FILTER_BY_STATUS] = {
      status: callTaskParams.taskStatusFilters,
    };
  }

  if (payload.filter && callTaskParams?.taskTypes?.length) {
    payload.filter[CallIntelTypes.TaskFilterTypeEnum.FILTER_BY_TYPE] = {
      type: callTaskParams.taskTypes,
    };
  }

  return payload;
};

/**
 * Given a Call Intel time period label (e.g., "Today", "This Week"),
 * returns the corresponding TimePeriodType.
 * If no direct mapping exists, returns TIMEPERIOD_TYPE_CUSTOM.
 */
export const convertTimePeriodLabelToEnum = (timePeriodLabel?: string): CallIntelTypes.TimePeriodTypeEnum => {
  return (
    CallIntelTimePeriodLabelToType[timePeriodLabel ?? ''] ?? CallIntelTypes.TimePeriodTypeEnum.TIMEPERIOD_TYPE_CUSTOM
  );
};

export const formatDateRangeForPayload = ({
  startDate,
  endDate,
  timePeriodLabel,
  timePeriod,
  timezone,
}: {
  startDate?: string;
  endDate?: string;
  timePeriodLabel?: string;
  timePeriod?: CallIntelTypes.TimePeriodTypeEnum;
  timezone?: string;
}): CallIntelTypes.DateRange => {
  // Get the current timezone offset in minutes
  const timezoneOffsetMinutes = dayjs().utcOffset();
  const start = dayjs(startDate).startOf('day').add(-timezoneOffsetMinutes, 'minute').format('YYYY-MM-DD HH:mm:ss.SSS');
  const end = dayjs(endDate).endOf('day').add(-timezoneOffsetMinutes, 'minute').format('YYYY-MM-DD HH:mm:ss.SSS');

  return {
    start,
    end,
    timePeriod: timePeriod ?? convertTimePeriodLabelToEnum(timePeriodLabel),
    timezone: timezone ?? dayjs.tz.guess(),
  };
};

export const generateCommonFilterPayload = ({
  filters,
  dateRangeParams,
}: CallIntelTypes.CommonFilterPayloadParams): CallIntelTypes.FilterPayload => {
  const { start, end } = formatDateRangeForPayload(filters);

  return {
    filter: {
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_DATE_RANGE]: {
        date_range: {
          end,
          start,
          ...(dateRangeParams?.timePeriod && { time_period: dateRangeParams.timePeriod, timezone: dayjs.tz.guess() }),
        },
      },
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_LOCATION]: {
        locations: filters.locations,
      },
    },
  };
};

export const generateDrillDownPayload = (drillDownOptions: CallIntelTypes.DrillDownOptions) => {
  const { index, key, value } = drillDownOptions;

  return {
    [index]: {
      [key]: value,
    },
  };
};

type ChartsPayload = { filter: Record<string, Filter> } & Omit<CallIntelTypes.ApiPayloadProps, 'filters'>;

export const generateChartsPayload = ({ drillDownOptions, filters }: CallIntelTypes.ApiPayloadProps): ChartsPayload => {
  const payload = generateCommonFilterPayload({ filters });

  if (filters.contactTypes?.length) {
    payload.filter[CallIntelTypes.FilterTypeEnum.FILTER_BY_CONTACT_TYPE] = {
      contact_type: filters.contactTypes,
    };
  }

  payload.filter = {
    ...payload.filter,
    [CallIntelTypes.FilterTypeEnum.FILTER_BY_CALL_STATUS]: {
      call_status: [CallIntelTypes.CallStatus.CALL_STATUS_COMPLETE],
    },
  };

  if (drillDownOptions) {
    payload.filter = {
      ...payload.filter,
      ...generateDrillDownPayload(drillDownOptions),
    };
  }

  return payload;
};

export const getFilteredEnumValues = <T extends Record<string, string>>(enumObj: T, excludeValue: T[keyof T]) =>
  Object.values(enumObj).filter((value) => value !== excludeValue);

export const fillTaskTypes = (
  partialTaskTypes: Partial<Record<TaskType, number>>,
  order: Exclude<TaskType, TaskType.TYPE_UNKNOWN>[]
): Record<Exclude<TaskType, TaskType.TYPE_UNKNOWN>, number> => {
  const filledTaskTypes = order.map((key) => [key, partialTaskTypes[key] ?? 0]);

  return Object.fromEntries(filledTaskTypes);
};

export const generateContactId = (call: CallIntelTypes.Call) => {
  if (call.person?.id) {
    return call.person.id;
  } else if (call.phoneNumber?.nationalNumber) {
    return getPhoneNumber(call.phoneNumber);
  }
  return `unknown-contact-${call.id}`;
};

export const getChangeType = (rateChange: number): CallIntelTypes.ChangeType => {
  if (isNaN(rateChange)) return 'noData';
  if (rateChange > 0) return 'increased';
  if (rateChange < 0) return 'decreased';
  return 'unchanged';
};

export const isOngoingLabel = (label: string): boolean => {
  const today = dayjs();

  switch (label) {
    case timePeriodLabels.today:
    case timePeriodLabels['this-week']:
    case timePeriodLabels['this-month']:
    case timePeriodLabels['this-quarter']:
      return true;

    // If it's a custom range, check if it includes today.
    default:
      if (label.includes('-')) {
        const [startLabel, endLabel] = label.split(' - ').map((date) => dayjs(date.trim()));
        return today.isBetween(startLabel, endLabel, 'day', '[]');
      }

      // For all other cases (e.g., "Last Week", "Last Month"), return false
      return false;
  }
};

export const getServiceQualityStatsPayload = ({
  filters,
  dateRangeParams,
}: CallIntelTypes.CommonFilterPayloadParams): CallIntelTypes.GetServiceQualityStatsIO['input'] => {
  const payload = generateCommonFilterPayload({ filters, dateRangeParams });

  payload.filter = {
    ...payload.filter,
    [CallIntelTypes.FilterTypeEnum.FILTER_BY_CALL_STATUS]: {
      call_status: [CallIntelTypes.CallStatus.CALL_STATUS_COMPLETE],
    },
    [CallIntelTypes.FilterTypeEnum.FILTER_BY_SERVICE_QUALITY]: {
      service_quality_flags: filters.serviceQualityFlags || [],
    },
  };

  return {
    ...payload,
    includeComparison: true,
  } as CallIntelTypes.GetServiceQualityStatsIO['input'];
};

export const generateFollowupsFilterPayload = (
  filters: CallIntelTypes.FollowupTaskTypeFilters
): CallIntelTypes.ListFollowupsIO['input'] => {
  const { officeUsers, contactTypes, taskTypes, taskStatus } = filters;

  const { start, end } = formatDateRangeForPayload(filters);

  const filter: CallIntelTypes.ListFollowupsIO['input']['filter'] = {
    [CallIntelTypes.FilterTypeEnum.FILTER_BY_DATE_RANGE]: {
      dateRange: {
        end,
        start,
      },
    },
    [CallIntelTypes.FilterTypeEnum.FILTER_BY_LOCATION]: {
      locations: filters.locations,
    },
  };

  if (officeUsers?.length) {
    filter[CallIntelTypes.FollowupTaskFilterEnum.FILTER_BY_OFFICE_USER] = {
      officeUserId: officeUsers,
    };
  }
  if (contactTypes?.length) {
    filter[CallIntelTypes.FollowupTaskFilterEnum.FILTER_BY_CONTACT_TYPE] = {
      contactType: contactTypes as CallIntelTypes.ContactTypeEnum[],
    };
  }
  if (taskTypes?.length) {
    filter[CallIntelTypes.FollowupTaskFilterEnum.FILTER_BY_TASK_TYPE] = {
      taskType: taskTypes,
    };
  }
  if (taskStatus?.length) {
    filter[CallIntelTypes.FollowupTaskFilterEnum.FILTER_BY_TASK_STATUS] = {
      taskStatus: taskStatus,
    };
  }

  return {
    filter,
  };
};
